import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { deleteBank, getBanks } from '../../../../api/endpoints/system';
import type { Bank } from '../../../../api/types/system';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  StatusChip,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../components/common';
import { BankFormDialog } from '../BankFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this project's
 *  pinned TypeScript 4.9.5). Confirmed via local-backend swagger + live curl (see
 *  api/types/system.ts header). Ortak `ListPageScaffold` + primitive'leri kullanır. */
export default function BankListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Bank | null>(null);

  const query = useQuery({ queryKey: ['system', 'banks'], queryFn: () => getBanks() });
  const rows: Bank[] = query.data?.banks ?? [];

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteBank(id),
    invalidateKeys: [['system', 'banks']],
    successMessage: 'Banka silindi.',
  });
  const deletingName = rows.find((r) => r.id === del.target)?.bankName ?? '';

  const columns: DataTableColumn<Bank>[] = [
    { id: 'name', header: 'Banka Adı', cell: (r) => r.bankName },
    { id: 'posCode', header: 'Pos Kodu', cell: (r) => r.posCode ?? '—' },
    { id: 'currency', header: 'Para Birimi', cell: (r) => r.currency ?? '—' },
    { id: 'default', header: 'Varsayılan', cell: (r) => (r.isDefault ? 'Evet' : 'Hayır') },
    { id: 'status', header: 'Durum', cell: (r) => <StatusChip active={r.isActive} /> },
  ];

  return (
    <ListPageScaffold
      title="Banka Ayarları"
      createLabel="Yeni Banka"
      onCreate={() => setCreateOpen(true)}
      query={query}
      rows={rows}
      columns={columns}
      getRowKey={(r) => r.id}
      emptyTitle="Kayıtlı banka bulunamadı."
      rowActions={(r) => (
        <RowActions onEdit={() => setEditing(r)} onDelete={() => del.request(r.id)} />
      )}
    >
      <BankFormDialog open={createOpen} bank={null} onClose={() => setCreateOpen(false)} />
      <BankFormDialog open={!!editing} bank={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Bankayı sil"
        description={`"${deletingName}" bankasını silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
